/**
 * Removing saved teachers on Review Teachers (12 Oct review, point 5). A teacher's allocations
 * live in `subject.teacherId`, and the onboarding bulk import deletes teachers BEFORE it reads
 * subject rows, so the delete cannot free them in the same request: an allocated teacher was
 * refused ("still used in Teacher Allocation") and stayed. On the AI server their cells are
 * cleared first, one `PATCH onboarding/allocation/cell/:subjectId/teacher {teacherId: null}`
 * each, and the delete follows.
 */
import type {AllocationServerGrid} from '../types/allocationServer'

import {allocationServerApi, isAllocationUnavailable, toAllocationError} from './allocationServerApi'

type Dispatch = (action: any) => unknown

/** Cells cleared at once: a teacher can hold dozens, the whole list hundreds. */
const UNASSIGN_CONCURRENCY = 4

/** The subject ids (cells) of `grid` held by one of `teacherIds`. */
export const assignedCellsOf = (
  grid: Pick<AllocationServerGrid, 'branches'> | null | undefined,
  teacherIds: ReadonlySet<number>
): number[] => {
  const ids = new Set<number>()
  ;(grid?.branches ?? []).forEach((branch) =>
    (branch?.rows ?? []).forEach((row) =>
      Object.values(row?.cells ?? {}).forEach((cell) => {
        const teacherId = Number(cell?.teacherId)
        const subjectId = Number(cell?.subjectId)
        if (teacherIds.has(teacherId) && Number.isFinite(subjectId) && subjectId > 0) {
          ids.add(subjectId)
        }
      })
    )
  )
  return [...ids]
}

/**
 * Clears every allocation cell of the saved teachers `teacherIds` on the server. Resolves with
 * the subject ids cleared; `unavailable` when the allocation routes do not exist (non-AI
 * servers, where the delete goes ahead as before). Never rejects: a cell that could not be
 * cleared leaves its teacher allocated, and the delete then reports that teacher as in use.
 */
export const unassignSavedTeachers = async (
  dispatch: Dispatch,
  teacherIds: readonly number[]
): Promise<{cleared: number[]; unavailable: boolean}> => {
  const wanted = new Set(teacherIds.filter((id) => Number.isFinite(id) && id > 0))
  if (wanted.size === 0) return {cleared: [], unavailable: false}
  let grid: AllocationServerGrid
  try {
    grid = await allocationServerApi.grid(dispatch, undefined, 'assigned')
  } catch (error) {
    return {cleared: [], unavailable: isAllocationUnavailable(toAllocationError(error))}
  }
  const pending = assignedCellsOf(grid, wanted)
  const cleared: number[] = []
  let next = 0
  const worker = async () => {
    while (next < pending.length) {
      const subjectId = pending[next++]
      try {
        await allocationServerApi.setTeacher(dispatch, subjectId, null)
        cleared.push(subjectId)
      } catch {
        // Left allocated: the teacher's delete is refused and the row stays, with the reason.
      }
    }
  }
  await Promise.all(
    Array.from({length: Math.min(UNASSIGN_CONCURRENCY, pending.length)}, () => worker())
  )
  return {cleared, unavailable: false}
}
